import { ArrowBack, Assignment as AddContractIcon, Save, Clear } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useQuery, { useDeferredQuery } from '../hooks/useQuery';
import useRequest from '../hooks/useRequest';
import useCashBookingReadiness from '../hooks/useCashBookingReadiness';
import { CashBookingRefusalBanner } from '../components/CashBookingRefusal';
import { useSumatorPageCollapse } from '../hooks/useSumatorPageCollapse';
import usePawnObjectDesk from '../hooks/usePawnObjectDesk';
import { useAuth } from '../contexts/AuthContext';
import { useCashDrawer } from '../contexts/CashDrawerContext';
import { useSumator } from '../contexts/SumatorContext';
import PageHeaderBanner from '../components/PageHeaderBanner';
import PawnObjectDesk from '../components/PawnObjectDesk';
import ContractStats from '../components/ContractStats';
import ContractPdfPreviewDialog from '../components/ContractPdfPreviewDialog';
import { ContractPrintBlockedError, useContractDocument } from '../utils/contractDocument';
import {
    canPrintLabels,
    needsPairing,
    printContractLabels,
    probeHardwareService,
} from '../domain/labelPrinting';
import { loadContractLabelJobs } from '../utils/contractLabels';
import {
    ELECTRONICS_TYPE,
    OTHERS_TYPE,
    buildPawnedObjectDescription,
    hasUnnamedElectronicObjects,
    sumEnabledObjectsLei,
} from '../domain/contracts/pawnObjectDesk';
import { clampDiscountPercent } from '../domain/contracts/contractDiscount';

/**
 * Computed, read-only fields. They use the outlined style of the editable ones, so the label
 * sits on the border: the theme's compact input padding leaves the filled variant no room for
 * a label above the value, which made the two overlap. The grey fill marks them read-only.
 */
const readOnlyFieldSlotProps = {
    input: { readOnly: true, sx: { bgcolor: 'action.hover' } },
    inputLabel: { shrink: true },
};

interface LatestExchangeRateResponse {
    data: { rateDate: string; exchangeRate: number } | null;
}

interface ClientData {
    ClNr: number;
    Nume: string;
    CNP?: string;
    Oras?: string;
    Str?: string;
    Nr?: string;
    Ap?: string;
    Tel?: string;
    Email?: string;
    NrContract?: string;
    [key: string]: unknown;
}

interface FormData {
    clientId: number;
    obiectAmanetat: string;
    sumainEuro: number;
    sumainLei: number;
    curs: number;
    durata: number;
    observatii: string;
    statut: string;
    comisionZi: number;
    reducere: number;
    tarifePref: boolean;
    dataScadenta: string;
    contractType: 'Lei' | 'Euro';
    printAfterSave: boolean;
    previewContract: boolean;
    durataPrelungita: boolean; // Extended duration option
    /** Delphi's *Speciala* box (BL-05): administrators only; no discount at payment. */
    specialRate: boolean;
    /**
     * Only the client may redeem the pledged items (`registru.personal` = `'P'`). Unticked, the
     * client agrees that a third person may: Delphi's *Da* box, which starts unticked, so this one
     * starts ticked.
     */
    personal: boolean;
}

interface FormErrors {
    clientId?: string;
    obiectAmanetat?: string;
    sumainEuro?: string;
    sumainLei?: string;
    curs?: string;
    durata?: string;
    observatii?: string;
    statut?: string;
}

const AddContract = () => {
    const { addLine } = useSumator();
    const [searchParams] = useSearchParams();
    const [selectedClient, setSelectedClient] = useState<ClientData | null>(null);
    const [formData, setFormData] = useState<FormData>({
        clientId: 0,
        obiectAmanetat: '',
        sumainEuro: 0,
        sumainLei: 0,
        curs: 5.0,
        durata: 30,
        observatii: '',
        statut: '',
        comisionZi: 0.5,
        reducere: 0,
        tarifePref: false,
        dataScadenta: '',
        contractType: 'Lei',
        printAfterSave: false,
        previewContract: false,
        durataPrelungita: false,
        specialRate: false,
        personal: true,
    });
    const [formErrors, setFormErrors] = useState<FormErrors>({});
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error' | 'warning' | 'info';
    }>({
        open: false,
        message: '',
        severity: 'success',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    // The number of a saved contract that must be reported (BL-15); the page leaves only once
    // the operator has acknowledged the warning, as Delphi's message box holds its form.
    const [highValueReportNumber, setHighValueReportNumber] = useState<string | null>(null);
    // The saved contract's document on screen (*Preview Contract*), or why it could not be
    // produced; like the warning above, the page leaves only once the operator closes it.
    const [savedContractDocument, setSavedContractDocument] = useState<{
        number: string;
        url: string | null;
        error: string | null;
        /** The browser refused to print it directly, so the viewer's print button has to. */
        printBlocked?: boolean;
    } | null>(null);
    const [contractStats, setContractStats] = useState({
        nrContracte: 0,
        valContracte: 0,
        nrContrExp: 0,
        valContrExp: 0,
        castigNet: 0,
    });
    const request = useRequest();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { API_BASE_URL, token, user } = useAuth();
    const { previewPdfDocument, printPdfDocument } = useContractDocument(t);
    // As in Delphi, only an administrator may write a special-rate contract (BL-05).
    const isAdmin = user?.role === 'ADMIN';

    // The cash point this workstation is registered as, resolved by App.tsx from the
    // station name and published on the cash drawer context. Null until that resolves;
    // App.tsx prompts for registration until it does.
    const { refreshBalance, cashPointId } = useCashDrawer();
    // A contract pays its loan out of the drawer: a station that cannot book it is told so on
    // opening the page, not when the filled-in contract is saved.
    const { refusalKey: bookingRefusalKey, ensureCanBook } = useCashBookingReadiness({
        checkOnMount: true,
    });

    // Collapse the sumator widget while on this page; put it back only if nothing else moved it
    useSumatorPageCollapse();

    // Load contract statistics for the footer
    useEffect(() => {
        const loadContractStats = async () => {
            try {
                const response: {
                    contracts?: number;
                    active?: number;
                    expired?: number;
                    activeValue?: number;
                    totalValue?: number;
                } = await request({
                    path: '/contracts/stats/summary',
                    method: 'GET',
                });

                if (response) {
                    // Calculate profit net (simplified calculation)
                    const profit = (response.totalValue ?? 0) * 0.1; // Assuming 10% profit margin

                    setContractStats({
                        nrContracte: response.active ?? 0,
                        valContracte: response.activeValue ?? 0,
                        nrContrExp: response.expired ?? 0,
                        valContrExp: (response.totalValue ?? 0) - (response.activeValue ?? 0),
                        castigNet: profit,
                    });
                }
            } catch (error) {
                console.error('Load contract stats error:', error);
            }
        };

        loadContractStats();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Back to the contracts page, with the client's contracts when a client was chosen.
    const returnToContracts = () => {
        if (formData.clientId) {
            navigate(`/contracts?clientId=${formData.clientId}`);
        } else {
            navigate('/contracts');
        }
    };

    // The warning comes first, as Delphi shows it before the contract report; a document
    // still to be seen keeps the page until it is closed.
    const acknowledgeHighValueReport = () => {
        setHighValueReportNumber(null);
        if (savedContractDocument === null) {
            returnToContracts();
        }
    };

    const closeSavedContractDocument = () => {
        if (savedContractDocument?.url) {
            window.URL.revokeObjectURL(savedContractDocument.url);
        }
        setSavedContractDocument(null);
        returnToContracts();
    };

    const describeDocumentError = (error: unknown) =>
        error instanceof Error ? error.message : t('contracts.documentPreview.loadError');

    const showSavedContractDocument = async (contractId: number, contractNumber: string) => {
        setSavedContractDocument({ number: contractNumber, url: null, error: null });
        try {
            const url = await previewPdfDocument(contractId);
            setSavedContractDocument({ number: contractNumber, url, error: null });
        } catch (error) {
            setSavedContractDocument({
                number: contractNumber,
                url: null,
                error: describeDocumentError(error),
            });
        }
    };

    /**
     * Previews or prints the saved contract, as the operator ticked. Resolves to true when a
     * dialog now holds the page: the preview, or the reason printing failed.
     */
    const presentSavedContract = async (
        contractId: number | undefined,
        contractNumber: string,
    ): Promise<boolean> => {
        if (contractId === undefined) {
            return false;
        }
        if (formData.previewContract) {
            // Printed from the preview when Print After Save is ticked as well.
            void showSavedContractDocument(contractId, contractNumber);
            return true;
        }
        if (!formData.printAfterSave) {
            return false;
        }
        try {
            await printPdfDocument(contractId);
            return false;
        } catch (error) {
            if (error instanceof ContractPrintBlockedError) {
                // Shown instead, already rendered: the viewer's own print button still works.
                setSavedContractDocument({
                    number: contractNumber,
                    url: window.URL.createObjectURL(error.blob),
                    error: null,
                    printBlocked: true,
                });
                return true;
            }
            setSavedContractDocument({
                number: contractNumber,
                url: null,
                error: describeDocumentError(error),
            });
            return true;
        }
    };

    // Helper function to show snackbar messages
    const showSnackbar = (
        message: string,
        severity: 'success' | 'error' | 'warning' | 'info' = 'info',
    ) => {
        setSnackbar({
            open: true,
            message,
            severity,
        });
    };

    const showPriceCalculationOffline = useCallback(() => {
        setSnackbar({
            open: true,
            message: t(
                'contracts.priceCalculationOffline',
                'Price calculation temporarily unavailable - please enter prices manually',
            ),
            severity: 'error',
        });
    }, [t]);

    // The rate starts from the latest stored BNR EUR rate, as Delphi's pawning form does
    // (`eTotCurs.Text := CurrentData.Er.EREUR`, UAmanetareNew.pas:1952); the operator can
    // still overwrite it, and a late answer never replaces a rate already typed. Until it
    // arrives, or if none is stored, the default stays.
    const exchangeRateEditedRef = useRef(false);
    const { data: latestExchangeRate } = useQuery<LatestExchangeRateResponse>({
        path: '/exchange-rates/latest',
    });
    useEffect(() => {
        const rate = latestExchangeRate?.data?.exchangeRate;
        if (rate !== undefined && rate > 0 && !exchangeRateEditedRef.current) {
            setFormData(prev => ({ ...prev, curs: rate }));
        }
    }, [latestExchangeRate]);

    const desk = usePawnObjectDesk({
        cashPointId,
        exchangeRate: formData.curs,
        extendedDuration: formData.durataPrelungita,
        prepareScratchTable: true,
        onPriceCalculationOffline: showPriceCalculationOffline,
    });
    const { metalObjects, electronicObjects } = desk;

    const handleSnackbarClose = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    // Get client ID from URL params if provided
    const clientIdParam = searchParams.get('clientId');

    // Only load the specific client if clientId is provided, otherwise don't load any clients
    const {
        data: clientsData,
        loading: clientsLoading,
        error: clientsError,
    } = useDeferredQuery<{
        client?: ClientData;
        clients?: ClientData[];
    }>({
        path: clientIdParam ? `/clients/${clientIdParam}` : '/clients?limit=0',
        method: 'GET',
        shouldExecute: !!clientIdParam,
    });

    // Create clients array with only the specific client or empty array
    const clients = useMemo(() => {
        if (clientIdParam && clientsData?.client) {
            // Single client response format
            return [clientsData.client];
        }
        if (clientIdParam && clientsData?.clients) {
            // Multiple clients response format (fallback)
            return clientsData.clients;
        }
        return [];
    }, [clientsData, clientIdParam]);

    // The discount starts from the shop's standard discount, raised to the client's card category
    // percentage, as Delphi's pawning form fills it in (UAmanetareNew.pas:1915-1950, BL-20). The
    // operator can still change it, and a late answer never replaces a figure already typed.
    const discountEditedRef = useRef(false);
    const { data: defaultDiscount } = useDeferredQuery<{ standardDiscountPercent?: number }>({
        path: `/contracts/default-discount/${clientIdParam}`,
        method: 'GET',
        shouldExecute: !!clientIdParam,
    });
    useEffect(() => {
        const percent = defaultDiscount?.standardDiscountPercent;
        if (percent !== undefined && !discountEditedRef.current) {
            setFormData(prev => ({ ...prev, reducere: percent }));
        }
    }, [defaultDiscount]);

    // Load specific client and set it as selected when clientId is provided
    useEffect(() => {
        if (clientIdParam && clients.length > 0) {
            const clientId = parseInt(clientIdParam, 10);
            const client = clients.find(c => c.ClNr === clientId) || clients[0];
            if (client) {
                setSelectedClient(client);
                setFormData(prev => ({ ...prev, clientId: client.ClNr }));
            }
        }
    }, [clientIdParam, clients]);

    // Calculate totals and due date when objects or duration change
    useEffect(() => {
        const totalLei = sumEnabledObjectsLei(metalObjects, electronicObjects);

        const totalEuro = totalLei / formData.curs;

        // Calculate due date with extended duration logic
        const today = new Date();
        const dueDate = new Date(today);
        let durationDays = formData.durata;

        // Extended duration logic (like original chbDurataPrelungita)
        if (formData.durataPrelungita) {
            durationDays = Math.max(durationDays, 60); // Minimum 60 days for extended
        }

        if (durationDays === 1) {
            dueDate.setDate(today.getDate() + 1);
        } else {
            dueDate.setDate(today.getDate() + durationDays - 1);
        }

        setFormData(prev => ({
            ...prev,
            sumainLei: totalLei,
            sumainEuro: totalEuro,
            dataScadenta: dueDate.toISOString().split('T')[0],
        }));
    }, [
        metalObjects,
        electronicObjects,
        formData.curs,
        formData.durata,
        formData.durataPrelungita,
    ]);

    // Check if client qualifies for preferential rates
    const checkPreferentialRates = async (clientId: number) => {
        try {
            // Implementation based on original DemnDeTarifePreferentiale function
            const response: { qualifies?: boolean; reason?: string } = await request({
                path: `/clients/${clientId}/preferential-rates`,
                method: 'GET',
            });

            if (response?.qualifies) {
                setFormData(prev => ({ ...prev, tarifePref: true }));
                showSnackbar(
                    t('contracts.preferentialRatesApproved') +
                        (response.reason ? `: ${response.reason}` : ''),
                    'success',
                );
            } else {
                setFormData(prev => ({ ...prev, tarifePref: false }));
            }
        } catch (error) {
            console.error('Check preferential rates error:', error);
            setFormData(prev => ({ ...prev, tarifePref: false }));
        }
    };

    const handleClientChange = (clientId: number) => {
        const client = clients.find(c => c.ClNr === clientId);
        setSelectedClient(client ?? null);
        setFormData(prev => ({ ...prev, clientId }));

        // Check for preferential rates when client is selected
        if (clientId > 0) {
            checkPreferentialRates(clientId);
        }
    };

    const handleInputChange = (field: keyof FormData, value: unknown) => {
        if (field === 'curs') {
            exchangeRateEditedRef.current = true;
        }
        if (field === 'reducere') {
            discountEditedRef.current = true;
        }
        setFormData(prev => ({ ...prev, [field]: value }));

        // Clear error when user starts typing
        if (formErrors[field as keyof FormErrors]) {
            setFormErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    // Keep the pawned-object description in step with the ticked rows (Delphi's
    // group_concat of denumire/quantity/UM). With nothing describable ticked the field
    // is left as typed.
    useEffect(() => {
        const objectDescription = buildPawnedObjectDescription(metalObjects, electronicObjects);
        if (objectDescription) {
            setFormData(prev => ({ ...prev, obiectAmanetat: objectDescription }));
        }
    }, [metalObjects, electronicObjects]);

    // Validation helpers to reduce validateForm complexity
    const collectFieldErrors = (): FormErrors => {
        const errors: FormErrors = {};
        if (!formData.clientId) errors.clientId = t('common.required');
        if (!formData.obiectAmanetat.trim()) errors.obiectAmanetat = t('common.required');
        if (formData.sumainEuro <= 0) errors.sumainEuro = t('common.invalidAmount');
        if (formData.sumainLei <= 0) errors.sumainLei = t('common.invalidAmount');
        if (formData.curs <= 0) errors.curs = t('common.invalidRate');
        if (formData.durata <= 0) errors.durata = t('common.invalidDuration');
        return errors;
    };

    const checkAnyObjectSelected = (): { valid: boolean; error?: string } => {
        const hasValidObjects = [
            ...metalObjects.filter(obj => obj.enabled),
            ...electronicObjects.filter(obj => obj.enabled),
        ].some(obj => obj.denumire.trim() !== '');
        if (!hasValidObjects) {
            return { valid: false, error: t('contracts.noObjectsSelected') };
        }
        return { valid: true };
    };

    const checkStoneCalculations = (): { valid: boolean; error?: string } => {
        const invalid = metalObjects.filter(
            obj =>
                obj.enabled &&
                obj.cantitate > 0 &&
                (obj.piatraPercent > 100 ||
                    obj.piatraPercent < 0 ||
                    obj.piatra > obj.cantitate ||
                    Math.abs(obj.piatra - (obj.cantitate * obj.piatraPercent) / 100) > 0.01),
        );
        if (invalid.length > 0) {
            return {
                valid: false,
                error: t(
                    'contracts.stoneCalculationError',
                    'Some metal objects have invalid stone percentages or calculations. Please check stone weight values.',
                ),
            };
        }
        return { valid: true };
    };

    const checkKaratRanges = (): { valid: boolean; error?: string } => {
        const invalid = metalObjects.filter(
            obj =>
                obj.enabled &&
                obj.tipMetal &&
                obj.karat > 0 &&
                obj.cantitate > 0 &&
                (obj.karat < 1 || obj.karat > 1000),
        );
        if (invalid.length > 0) {
            return {
                valid: false,
                error: t(
                    'contracts.invalidKaratRange',
                    'Some metal objects have invalid purity values. Purity must be between 1 and 1000.',
                ),
            };
        }
        return { valid: true };
    };

    const checkDuplicateMetalTypes = (): { valid: boolean; error?: string } => {
        const metalTypeCounts = metalObjects
            .filter(obj => obj.enabled && obj.cantitate > 0)
            .reduce(
                (acc, obj) => {
                    const key = `${obj.tipMetal}_${Math.floor(obj.karat)}`;
                    acc[key] = (acc[key] ?? 0) + 1;
                    return acc;
                },
                {} as Record<string, number>,
            );
        const hasDuplicates = Object.values(metalTypeCounts).some(count => count > 1);
        if (hasDuplicates) {
            return {
                valid: false,
                error: t(
                    'contracts.duplicateMetalTypes',
                    'Cannot put multiple objects of the same metal type and karat on one contract.',
                ),
            };
        }
        return { valid: true };
    };

    const checkObjectValues = (): { valid: boolean; error?: string } => {
        const validMetals = metalObjects.filter(
            obj => obj.enabled && obj.cantitate > 0 && obj.sumainLei > 0,
        );
        const validElectronics = electronicObjects.filter(
            obj => obj.enabled && obj.cantitate > 0 && obj.sumainLei > 0,
        );
        if (validMetals.length === 0 && validElectronics.length === 0) {
            return {
                valid: false,
                error: t(
                    'contracts.noValidObjects',
                    'At least one object must be enabled with valid quantity and value greater than 0.',
                ),
            };
        }
        return { valid: true };
    };

    const confirmElectronicsWithoutName = (): { valid: boolean; error?: string } => {
        if (hasUnnamedElectronicObjects(electronicObjects)) {
            const confirmed = Boolean(confirm(t('contracts.electronicsWithoutNameConfirm')));
            if (!confirmed) {
                return {
                    valid: false,
                    error: t(
                        'contracts.electronicsWithoutNameError',
                        'Please provide names for electronic objects or cancel to review.',
                    ),
                };
            }
        }
        return { valid: true };
    };

    const checkMinimumContractValue = (): { valid: boolean; error?: string } => {
        const totalContractValue =
            formData.contractType === 'Lei' ? formData.sumainLei : formData.sumainEuro;
        if (totalContractValue < 10) {
            return {
                valid: false,
                error: t(
                    'contracts.minimumValueError',
                    'Contract value is below minimum allowed amount.',
                ),
            };
        }
        return { valid: true };
    };

    const confirmDefaultNames = (): { valid: boolean; error?: string } => {
        const objectsWithDefaultNames = [
            ...metalObjects.filter(
                obj => obj.enabled && obj.denumire.trim().toLowerCase().includes('default'),
            ),
            ...electronicObjects.filter(
                obj => obj.enabled && obj.denumire.trim().toLowerCase().includes('default'),
            ),
        ];
        if (objectsWithDefaultNames.length > 0) {
            const confirmed = Boolean(
                confirm(
                    t(
                        'contracts.defaultNamesConfirm',
                        'Some objects have default names. Continue anyway?',
                    ),
                ),
            );
            if (!confirmed) {
                return {
                    valid: false,
                    error: t(
                        'contracts.defaultNamesError',
                        'Please provide specific names for objects or cancel to review.',
                    ),
                };
            }
        }
        return { valid: true };
    };

    const validateForm = (): boolean => {
        const fieldErrors = collectFieldErrors();
        const validationErrors: string[] = [];

        // Collect field validation errors
        if (Object.keys(fieldErrors).length > 0) {
            setFormErrors(fieldErrors);
            Object.entries(fieldErrors).forEach(([field, error]) => {
                if (error) {
                    validationErrors.push(`${getFieldLabel(field)}: ${error}`);
                }
            });
        }

        // Run all object validation checks
        const validators = [
            checkAnyObjectSelected,
            checkStoneCalculations,
            checkKaratRanges,
            checkDuplicateMetalTypes,
            checkObjectValues,
            confirmElectronicsWithoutName,
            checkMinimumContractValue,
            confirmDefaultNames,
        ];

        for (const validate of validators) {
            const result = validate();
            if (!result.valid && result.error) {
                validationErrors.push(result.error);
            }
        }

        // If there are any validation errors, show them all
        if (validationErrors.length > 0) {
            showSnackbar(
                t('common.validationErrors') + '\n• ' + validationErrors.join('\n• '),
                'error',
            );
            return false;
        }

        setFormErrors({});
        return true;
    };

    // Helper function to get user-friendly field labels
    const getFieldLabel = (field: string): string => {
        const labels: Record<string, string> = {
            clientId: t('contracts.client'),
            obiectAmanetat: t('contracts.objectPledged'),
            sumainEuro: t('contracts.amountEuro'),
            sumainLei: t('contracts.amountLei'),
            curs: t('contracts.exchangeRate'),
            durata: t('contracts.duration'),
            observatii: t('contracts.observations'),
            statut: t('contracts.status'),
        };
        return labels[field] ?? field;
    };

    /**
     * Print the contract's object labels on the workstation's label printer.
     *
     * Best effort by design. The contract is already saved by the time this runs, so
     * every failure — no hardware service installed, this workstation not paired,
     * printer offline — is reported as a warning and nothing more. The operator can
     * always reprint from the Listings page, which is also where pairing happens.
     */
    const printContractObjectLabels = async (contractNr: number | undefined) => {
        if (contractNr === undefined) {
            return;
        }

        try {
            const browserFetch = window.fetch.bind(window);
            const status = await probeHardwareService(browserFetch);

            // Silent when there is no usable service: an unpaired or absent service is
            // an installation matter, not something to interrupt a completed contract
            // with. The Listings card is where it gets explained and fixed.
            if (status.state !== 'ready' || !canPrintLabels(status) || needsPairing(status)) {
                return;
            }

            const outcome = await printContractLabels(
                () =>
                    loadContractLabelJobs(
                        API_BASE_URL,
                        { Authorization: `Bearer ${token}` },
                        contractNr,
                        status.health.installationId,
                    ),
                browserFetch,
                status,
            );

            if (outcome.kind === 'completed' && outcome.summary.failed > 0) {
                showSnackbar(t('listings.label.printerUnavailable'), 'warning');
            }
        } catch {
            showSnackbar(t('listings.label.buildFailed'), 'warning');
        }
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            // validateForm now handles setting the alertMessage with specific errors
            return;
        }

        // Asked again here, not only when the page opened: a handover may have closed the
        // session while the form was being filled in.
        const refusalKey = await ensureCanBook();
        if (refusalKey) {
            showSnackbar(t(refusalKey), 'warning');
            return;
        }

        setShowConfirmDialog(true);
    };

    const handleConfirmSubmit = async () => {
        setShowConfirmDialog(false);
        setIsSubmitting(true);
        // Clear any existing snackbar
        setSnackbar(prev => ({ ...prev, open: false }));

        try {
            const contractData = {
                clientId: formData.clientId,
                obiectAmanetat: formData.obiectAmanetat,
                sumainEuro: Number(formData.sumainEuro.toFixed(2)),
                sumainLei: Number(formData.sumainLei.toFixed(0)),
                curs: Number(formData.curs),
                durata: Number(formData.durata),
                observatii: formData.observatii,
                contractType: formData.contractType,
                comisionZi: formData.comisionZi,
                reducere: formData.reducere,
                tarifePref: formData.tarifePref,
                specialRate: isAdmin && formData.specialRate,
                thirdPartyRedemption: !formData.personal,
                printAfterSave: formData.printAfterSave,
                previewContract: formData.previewContract,
                dataScadenta: formData.dataScadenta,
                // The register, not a session: the server books the payout into the session
                // open there now (a session number kept since login was stale after a handover).
                cashPointId: cashPointId ?? undefined,
                metalObjects: metalObjects
                    .filter(obj => obj.enabled && obj.denumire.trim())
                    .map(obj => ({
                        denumire: obj.denumire,
                        tipMetal: obj.tipMetal,
                        karat: obj.karat,
                        categoria: obj.categoria,
                        cantitate: obj.cantitate,
                        um: obj.um,
                        pretUnitar: obj.pretUnitar,
                        sumainEuro: obj.sumainEuro,
                        sumainLei: obj.sumainLei,
                        piatraPercent: obj.piatraPercent,
                        piatra: obj.piatra,
                        propus: obj.propus,
                        px: obj.px,
                        tip: 'metal',
                    })),
                electronicObjects: electronicObjects
                    .filter(obj => obj.enabled && obj.denumire.trim())
                    .map(obj => {
                        const getTipCode = (tip: string) => {
                            if (tip === ELECTRONICS_TYPE) return 'EL';
                            if (tip === OTHERS_TYPE) return 'AL';
                            return tip;
                        };

                        return {
                            denumire: obj.denumire,
                            tip: getTipCode(obj.tip),
                            cantitate: obj.cantitate,
                            um: obj.um,
                            pretUnitar: obj.pretUnitar,
                            sumainEuro: obj.sumainEuro,
                            sumainLei: obj.sumainLei,
                            tip_obiect: getTipCode(obj.tip),
                        };
                    }),
            };

            // The shape goes on the call rather than on an assertion after it: `request` is
            // generic, so it already returns exactly this, and asserting it again asserts
            // nothing.
            const response = await request<{
                contract?: { id?: number; number?: string };
                highValueReport?: boolean;
            } | null>({
                path: '/contracts',
                method: 'POST',
                params: contractData,
            });

            if (response) {
                // Refresh cash drawer balance immediately after successful contract creation
                refreshBalance();
                
                // Add to sumator (Negative amount because the shop gives money to the customer)
                addLine(t('sumator.newContract'), -contractData.sumainLei);

                showSnackbar(t('contracts.createSuccess'), 'success');

                // Object labels print automatically on save, as the legacy does
                // (documentation/legacy_delphi/label_printing_audit.md §2). Deliberately not
                // awaited into the success path: the contract is already saved, so a
                // missing hardware service or an offline printer must never be able to
                // make a successful contract look like a failure.
                void printContractObjectLabels(response.contract?.id);

                const contractNumber = response.contract?.number ?? '';
                const documentHoldsPage = await presentSavedContract(
                    response.contract?.id,
                    contractNumber,
                );

                if (response.highValueReport) {
                    setHighValueReportNumber(contractNumber);
                } else if (!documentHoldsPage) {
                    // Navigate back to contracts page after short delay
                    setTimeout(returnToContracts, 1500);
                }
            }
        } catch (error: unknown) {
            console.error('Create contract error:', error);
            showSnackbar(
                error instanceof Error ? error.message : t('contracts.createError'),
                'error',
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancelConfirm = () => {
        setShowConfirmDialog(false);
    };

    const contractTypes = ['Lei', 'Euro'];

    // Show error state if no clientId is provided (page should not be accessed without client)
    if (!clientIdParam) {
        return (
            <Box p={3}>
                <Alert severity="warning">
                    {t(
                        'contracts.clientRequired',
                        'Please select a client from the contracts page first.',
                    )}
                </Alert>
                <Box mt={2}>
                    <Button variant="outlined" onClick={() => navigate('/contracts')}>
                        {t('common.back', 'Back to Contracts')}
                    </Button>
                </Box>
            </Box>
        );
    }

    // Show loading state while the specific client is loading
    if (clientsLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <Typography>{t('common.loading', 'Loading...')}</Typography>
            </Box>
        );
    }

    // Show error state if client failed to load
    if (clientsError) {
        return (
            <Box p={3}>
                <Alert severity="error">
                    {t('common.error', 'An error occurred')}: {String(clientsError)}
                </Alert>
                <Box mt={2}>
                    <Button variant="outlined" onClick={() => navigate('/contracts')}>
                        {t('common.back', 'Back to Contracts')}
                    </Button>
                </Box>
            </Box>
        );
    }

    // Show error state if client was not found
    if (clientIdParam && clients.length === 0) {
        return (
            <Box p={3}>
                <Alert severity="error">{t('contracts.clientNotFound', 'Client not found.')}</Alert>
                <Box mt={2}>
                    <Button variant="outlined" onClick={() => navigate('/contracts')}>
                        {t('common.back', 'Back to Contracts')}
                    </Button>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Paper sx={{ p: 3 }}>
                <PageHeaderBanner
                    icon={<AddContractIcon />}
                    title={t('contracts.add')}
                    subtitle={t('contracts.addPageSubtitle')}
                    accentColor="#4338ca"
                    actions={
                        <Button variant="outlined" startIcon={<ArrowBack />} onClick={returnToContracts}>
                            {t('common.back')}
                        </Button>
                    }
                />

                <CashBookingRefusalBanner refusalKey={bookingRefusalKey} />

                <Stack spacing={3}>
                    {/* Client Selection */}
                    <Card>
                        <CardHeader title={t('clients.title')} />
                        <CardContent>
                            <FormControl fullWidth error={!!formErrors.clientId}>
                                <InputLabel>{t('contracts.client')}</InputLabel>
                                <Select
                                    value={formData.clientId > 0 ? formData.clientId : ''}
                                    onChange={e => handleClientChange(Number(e.target.value))}
                                    label={t('contracts.client')}
                                    disabled={true} // Always disabled since client is fixed
                                    sx={{
                                        '& .MuiSelect-select': {
                                            backgroundColor: 'action.hover',
                                            color: 'text.primary',
                                        },
                                    }}
                                >
                                    {clients.map(client => (
                                        <MenuItem key={client.ClNr} value={client.ClNr}>
                                            {client.Nume} {client.CNP ? `(${client.CNP})` : ''}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Box mt={1}>
                                <Typography variant="caption" color="text.secondary">
                                    {t(
                                        'contracts.clientFixedFromSelection',
                                        'Client selected from contracts page and cannot be changed',
                                    )}
                                </Typography>
                            </Box>
                            {selectedClient && (
                                <Box mt={2}>
                                    <Typography variant="body2">
                                        <strong>{t('clients.name')}:</strong> {selectedClient.Nume}
                                    </Typography>
                                    {selectedClient.CNP && (
                                        <Typography variant="body2">
                                            <strong>{t('clients.cnp')}:</strong>{' '}
                                            {selectedClient.CNP}
                                        </Typography>
                                    )}
                                    {selectedClient.Tel && (
                                        <Typography variant="body2">
                                            <strong>{t('clients.phone')}:</strong>{' '}
                                            {selectedClient.Tel}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </CardContent>
                    </Card>

                    {/* Tabs for Objects */}
                    <Card>
                        <CardHeader title={t('contracts.items')} />
                        <CardContent>
                            <PawnObjectDesk desk={desk} />
                        </CardContent>
                    </Card>

                    {/* Contract Details */}
                    <Card>
                        <CardHeader title={t('contracts.contractDetails')} />
                        <CardContent>
                            <Stack spacing={3}>
                                <Stack direction="row" spacing={3}>
                                    <TextField
                                        fullWidth
                                        label={t('contracts.item')}
                                        value={formData.obiectAmanetat}
                                        onChange={e =>
                                            handleInputChange('obiectAmanetat', e.target.value)
                                        }
                                        error={!!formErrors.obiectAmanetat}
                                        helperText={formErrors.obiectAmanetat}
                                        disabled={isSubmitting}
                                    />
                                    <FormControl sx={{ minWidth: 120 }}>
                                        <InputLabel>{t('contracts.contractType')}</InputLabel>
                                        <Select
                                            value={formData.contractType}
                                            onChange={e =>
                                                handleInputChange('contractType', e.target.value)
                                            }
                                            label={t('contracts.contractType')}
                                            disabled={isSubmitting}
                                        >
                                            {contractTypes.map(type => (
                                                <MenuItem key={type} value={type}>
                                                    {type}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Stack>
                                <Stack direction="row" spacing={3}>
                                    <TextField
                                        label={t('contracts.exchangeRate')}
                                        type="number"
                                        value={formData.curs}
                                        onChange={e =>
                                            handleInputChange('curs', Number(e.target.value))
                                        }
                                        error={!!formErrors.curs}
                                        helperText={formErrors.curs}
                                        disabled={isSubmitting}
                                        sx={{ width: 120 }}
                                    />
                                    <TextField
                                        label={t('contracts.duration')}
                                        type="number"
                                        value={formData.durata}
                                        onChange={e =>
                                            handleInputChange('durata', Number(e.target.value))
                                        }
                                        error={!!formErrors.durata}
                                        helperText={formErrors.durata}
                                        disabled={isSubmitting}
                                        sx={{ width: 120 }}
                                    />
                                    <TextField
                                        label={t('contracts.dueDate')}
                                        type="date"
                                        value={formData.dataScadenta}
                                        slotProps={readOnlyFieldSlotProps}
                                        sx={{ width: 160 }}
                                    />
                                    <TextField
                                        label={t('contracts.commission')}
                                        type="number"
                                        value={formData.comisionZi}
                                        onChange={e =>
                                            handleInputChange('comisionZi', Number(e.target.value))
                                        }
                                        disabled={isSubmitting}
                                        sx={{ width: 120 }}
                                        slotProps={{ htmlInput: { step: 0.1, min: 0, max: 100 } }}
                                    />
                                    <TextField
                                        label={t('contracts.discount')}
                                        type="number"
                                        value={formData.reducere}
                                        onChange={e =>
                                            handleInputChange(
                                                'reducere',
                                                clampDiscountPercent(Number(e.target.value)),
                                            )
                                        }
                                        disabled={isSubmitting}
                                        sx={{ width: 120 }}
                                        slotProps={{ htmlInput: { step: 1, min: 0, max: 100 } }}
                                    />
                                </Stack>
                                <Stack direction="row" spacing={3}>
                                    <TextField
                                        fullWidth
                                        label={
                                            formData.contractType === 'Lei'
                                                ? t('contracts.totalAmountLei')
                                                : t('contracts.totalAmountEur')
                                        }
                                        type="number"
                                        value={
                                            formData.contractType === 'Lei'
                                                ? formData.sumainLei.toFixed(0)
                                                : formData.sumainEuro.toFixed(2)
                                        }
                                        slotProps={readOnlyFieldSlotProps}
                                    />
                                    {formData.contractType === 'Lei' && (
                                        <TextField
                                            fullWidth
                                            label={t('contracts.totalAmountEur')}
                                            type="number"
                                            value={formData.sumainEuro.toFixed(2)}
                                            slotProps={readOnlyFieldSlotProps}
                                        />
                                    )}
                                    {formData.contractType === 'Euro' && (
                                        <TextField
                                            fullWidth
                                            label={t('contracts.totalAmountLei')}
                                            type="number"
                                            value={formData.sumainLei.toFixed(0)}
                                            slotProps={readOnlyFieldSlotProps}
                                        />
                                    )}
                                </Stack>
                                <TextField
                                    fullWidth
                                    label={t('contracts.observations')}
                                    multiline
                                    rows={3}
                                    value={formData.observatii}
                                    onChange={e => handleInputChange('observatii', e.target.value)}
                                    disabled={isSubmitting}
                                />
                                <Stack direction="row" spacing={2}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={formData.tarifePref}
                                                onChange={e =>
                                                    handleInputChange(
                                                        'tarifePref',
                                                        e.target.checked,
                                                    )
                                                }
                                                disabled={isSubmitting}
                                            />
                                        }
                                        label={t('contracts.preferentialRates')}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={formData.durataPrelungita}
                                                onChange={e =>
                                                    handleInputChange(
                                                        'durataPrelungita',
                                                        e.target.checked,
                                                    )
                                                }
                                                disabled={isSubmitting}
                                            />
                                        }
                                        label={t('contracts.extendedDuration')}
                                    />
                                    {isAdmin && (
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={formData.specialRate}
                                                    onChange={e =>
                                                        handleInputChange(
                                                            'specialRate',
                                                            e.target.checked,
                                                        )
                                                    }
                                                    disabled={isSubmitting}
                                                />
                                            }
                                            label={t('contracts.specialRate.checkbox')}
                                        />
                                    )}
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={formData.printAfterSave}
                                                onChange={e =>
                                                    handleInputChange(
                                                        'printAfterSave',
                                                        e.target.checked,
                                                    )
                                                }
                                                disabled={isSubmitting}
                                            />
                                        }
                                        label={t('contracts.printAfterSave')}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={formData.previewContract}
                                                onChange={e =>
                                                    handleInputChange(
                                                        'previewContract',
                                                        e.target.checked,
                                                    )
                                                }
                                                disabled={isSubmitting}
                                            />
                                        }
                                        label={t('contracts.previewContract')}
                                    />
                                    <Tooltip title={t('contracts.personalHelp')} describeChild>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    data-testid="add-contract-personal"
                                                    checked={formData.personal}
                                                    onChange={e =>
                                                        handleInputChange(
                                                            'personal',
                                                            e.target.checked,
                                                        )
                                                    }
                                                    disabled={isSubmitting}
                                                />
                                            }
                                            label={t('contracts.personal')}
                                        />
                                    </Tooltip>
                                </Stack>
                            </Stack>
                        </CardContent>
                    </Card>
                </Stack>

                {/* Action Buttons */}
                <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
                    <Button
                        variant="outlined"
                        startIcon={<Clear />}
                        onClick={returnToContracts}
                        disabled={isSubmitting}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        data-testid="add-contract-submit-button"
                        variant="contained"
                        startIcon={<Save />}
                        onClick={handleSubmit}
                        disabled={isSubmitting || !selectedClient || bookingRefusalKey !== null}
                    >
                        {isSubmitting ? t('common.saving') : t('contracts.create')}
                    </Button>
                </Box>
            </Paper>

            {/* Contract Statistics Panel - Like original Delphi */}
            <Paper sx={{ p: 2, mt: 2, bgcolor: 'grey.100' }}>
                <ContractStats
                    stats={contractStats}
                    translations={{
                        activeContracts: t('contracts.stats.activeContracts'),
                        activeValue: t('contracts.stats.activeValue'),
                        expiredContracts: t('contracts.stats.expiredContracts'),
                        expiredValue: t('contracts.stats.expiredValue'),
                        netProfit: t('contracts.stats.netProfit'),
                        currencyLei: t('contracts.currencyLei'),
                    }}
                />
            </Paper>

            <Dialog
                open={highValueReportNumber !== null}
                onClose={acknowledgeHighValueReport}
                aria-labelledby="high-value-report-title"
            >
                <DialogTitle id="high-value-report-title">
                    {t('contracts.highValueReportTitle')}
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning">
                        {t('contracts.highValueReportMessage', {
                            number: highValueReportNumber ?? '',
                        })}
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={acknowledgeHighValueReport} variant="contained">
                        {t('common.close')}
                    </Button>
                </DialogActions>
            </Dialog>

            <ContractPdfPreviewDialog
                open={savedContractDocument !== null && highValueReportNumber === null}
                contractNumber={savedContractDocument?.number ?? ''}
                url={savedContractDocument?.url ?? null}
                error={savedContractDocument?.error ?? null}
                printBlocked={savedContractDocument?.printBlocked ?? false}
                onClose={closeSavedContractDocument}
            />

            {/* Confirmation Dialog */}
            <Dialog
                open={showConfirmDialog}
                onClose={handleCancelConfirm}
                aria-labelledby="confirm-dialog-title"
                aria-describedby="confirm-dialog-description"
            >
                <DialogTitle id="confirm-dialog-title">{t('contracts.confirmCreate')}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="confirm-dialog-description">
                        {t('contracts.confirmCreateMessage')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelConfirm} color="primary">
                        {t('common.no')}
                    </Button>
                    <Button onClick={handleConfirmSubmit} color="primary" variant="contained">
                        {t('common.yes')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for floating notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={snackbar.severity === 'success' ? 3000 : 6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    <Box component="div" sx={{ whiteSpace: 'pre-line' }}>
                        {snackbar.message}
                    </Box>
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AddContract;
